import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Map, List } from 'immutable';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';

import connectOptions, { mergeProps } from '../utils/connectOptions';
import { Project } from '../components/Project';
import config from '../config';
import { loadPage, reset, clearError } from '../actions/project/main';
import { sortTarget, saveTarget, changeTarget, loadTargets, loadSortedTargets } from '../actions/project/target';
import { clearTemplatePopupError } from '../actions/project/templatePopup';
import {
  updateApprovals,
  insertApprovalRow,
  switchApprovalRowToEdit,
  sortApproval,
  deleteApproval,
  saveApproval,
} from '../actions/project/approval';
import { openInternalLink } from '../utils/url';
import { showError } from '../utils/MessagePopup';
import { isDeepChanged, getChanged } from '../utils/ChangeSpy';
import { isResearcher } from '../utils/checkPermissions';

import If from '../components/helpers/If';
import deleteApprovalConfirmation from './services/deleteApprovalConfirmation';
import downloadFile from '../utils/downloadFile';
import { usePopupContext } from '../contexts/PopupContext';

const types = config.values.getIn(['project', 'types']);
const targetColumns = config.tables.getIn(['project', 'target', 'columns']);
const approvalColumns = config.tables.getIn(['project', 'approvalFinancial', 'columns']);
const DETAILS_POPUP = 'ProjectDetailsPopup';

/**
 * State container component for project page.
 *
 * @param props {Object}.
 * @param projectData {Immutable.Map} Project information.
 * @param industries {Immutable.List} Industry list.
 * @param users {Immutable.List} User list.
 * @param approvals {Immutable.List} Approval list.
 * @param templates {Immutable.List} Template list.
 * @param targets {Immutable.List} Target list.
 * @param common {Immutable.Map} Common object.
 * @param savePending {Boolean} Saving status.
 */
const ProjectContainer = props => {
  const {
    changeTarget,
    clearError,
    clearTemplatePopupError,
    common,
    deleteApproval,
    history,
    insertApprovalRow,
    loadPage,
    loadSortedTargets,
    loadTargets,
    match,
    projectData,
    reset,
    saveApproval,
    saveTarget,
    sortApproval,
    sortTarget,
    switchApprovalRowToEdit,
    targets,
    targetsPagination,
    templatePopup,
    templates,
    updateApprovals,
    user,
    users,
  } = props;

  const { openPopup, closePopup, onClosePopup, getPopupName } = usePopupContext();

  const [lazyLoadingIsEnabled, setLazyLoadingIsEnabled] = useState(true);
  // Saved project details shown in the widget while the edit popup is open, so unsaved edits stay hidden.
  const [savedDetails, setSavedDetails] = useState(null);
  const [all, setAll] = useState(0);

  const tableContainerRef = useRef(null);
  const hasMountedRef = useRef(false);

  const userHasAccess = user ? !isResearcher(user.getIn(['roles', 0, 'slug'], null)) : false;

  const handleTableRef = useCallback(tableContainer => {
    tableContainerRef.current = tableContainer;
  }, []);

  const handleEditEnd = useCallback(() => {
    setSavedDetails(null);
  }, []);

  const reopenDetailsPopup = useCallback(() => {
    openPopup(DETAILS_POPUP, { onClose: handleEditEnd });
  }, [openPopup, handleEditEnd]);

  const showErrorsPopup = useCallback(() => {
    const errors = common.get('errors');
    // Template upload is opened from the details popup only, so its errors return there as well.
    const isDetailsPopupOpen = [DETAILS_POPUP, 'TemplatePopup'].includes(getPopupName());

    if (errors && errors.size > 0) {
      const handleErrorClose = () => {
        clearError();
        if (isDetailsPopupOpen) {
          reopenDetailsPopup();
        } else {
          closePopup();
        }
      };

      showError(openPopup, errors, handleErrorClose);
      if (isDetailsPopupOpen) {
        onClosePopup(reopenDetailsPopup);
      }
    } else if (templatePopup) {
      const templatePopupErrors = templatePopup.get('errors');

      if (templatePopupErrors && templatePopupErrors.size > 0) {
        const handleTemplatePopupErrorClose = () => {
          clearTemplatePopupError();
          if (isDetailsPopupOpen) {
            reopenDetailsPopup();
          } else {
            closePopup();
          }
        };

        showError(openPopup, templatePopupErrors, handleTemplatePopupErrorClose);
        if (isDetailsPopupOpen) {
          onClosePopup(reopenDetailsPopup);
        }
      }
    }
  }, [
    common,
    templatePopup,
    getPopupName,
    clearTemplatePopupError,
    clearError,
    closePopup,
    openPopup,
    onClosePopup,
    reopenDetailsPopup,
  ]);

  const handleScroll = useCallback(() => {
    const tableContainer = tableContainerRef.current;

    if (!tableContainer) return;

    const rows = tableContainer.getElementsByTagName('tr');
    const lastRow = rows[rows.length - 1];

    if (!lastRow) return;

    if (lastRow.getBoundingClientRect().bottom <= window.innerHeight && lazyLoadingIsEnabled) {
      const currentPage = targetsPagination.get('currentPage');
      const { targetSortField, targetSortDirection } = common.toJS();
      const transformedField = targetSortField?.replace(/([A-Z])/g, letter => `_${letter.toLowerCase()}`);
      const sort = targetSortDirection === 'down' ? `-${transformedField}` : transformedField;

      setLazyLoadingIsEnabled(false);
      if (currentPage < targetsPagination.get('totalPages')) {
        loadTargets(
          match.params.projectId,
          {
            sort: targetSortField ? sort : null,
            page: currentPage + 1,
            all,
          },
          {
            afterSuccess: () => {
              setLazyLoadingIsEnabled(true);
            },
          },
        );
      }
    }
  }, [lazyLoadingIsEnabled, all, targetsPagination, common, match, loadTargets]);

  const handleRowClick = useCallback(
    ({ row, type }) => {
      if (type === types.get('APPROVALS')) {
        switchApprovalRowToEdit(row.get('index'));
      }
    },
    [switchApprovalRowToEdit],
  );

  const handleRowDoubleClick = useCallback(({ row, type }) => {
    const id = row.get('id');
    const projectId = row.get('projectId');

    if (type === types.get('APPROVALS')) {
      openInternalLink(`/project/${projectId}/approval/${id}`, false);
    } else if (type === types.get('TARGETS')) {
      openInternalLink(`/company/${id}`);
    }
  }, []);

  const handleNoApprovalDoubleClick = useCallback(() => {
    const projectId = projectData.get('id');

    openInternalLink(`/project/${projectId}/approval/no_approval_lists`, false);
  }, [projectData]);

  const handleRowInsert = useCallback(
    type => {
      if (type === types.get('APPROVALS')) {
        insertApprovalRow();
      }
    },
    [insertApprovalRow],
  );

  const handleDeleteApprovalWrapper = useCallback(
    (projectId, approvalId) => {
      deleteApproval({
        projectId,
        approvalId,
        field: common.get('approvalSortField'),
        direction: common.get('approvalSortDirection'),
      });
    },
    [deleteApproval, common],
  );

  const deleteApprovalWithConfirmation = useMemo(
    () =>
      deleteApprovalConfirmation({
        deleteApproval: handleDeleteApprovalWrapper,
        user,
        openPopup,
        closePopup,
      }),
    [handleDeleteApprovalWrapper, user, openPopup, closePopup],
  );

  const handleApprovalRowDelete = useCallback(
    (event, { approval }) => {
      deleteApprovalWithConfirmation(projectData.get('id'), approval.get('id'), approval.get('numberTargets', 0));
    },
    [deleteApprovalWithConfirmation, projectData],
  );

  const handleTextChange = useCallback(
    ({ event, data: { type, row } }) => {
      const { name, value } = event.target;

      if (type === types.get('APPROVALS')) {
        updateApprovals(row.get('index'), name, value);
      }
    },
    [updateApprovals],
  );

  const handleSort = useCallback(
    (event, { type, direction, field }) => {
      if (type === types.get('TARGETS')) {
        const { projectId } = match.params;
        const { currentPage, perPage } = targetsPagination.toJS();
        const transformedField = field.replace(/([A-Z])/g, letter => `_${letter.toLowerCase()}`);
        const sort = direction === 'down' ? `-${transformedField}` : transformedField;
        const limit = currentPage * perPage;

        loadSortedTargets(projectId, { sort, limit, all });
        sortTarget(field, direction);
      } else if (type === types.get('APPROVALS')) {
        sortApproval(field, direction);
      }
    },
    [match, targetsPagination, all, loadSortedTargets, sortTarget, sortApproval],
  );

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  const handleReload = useCallback(() => {
    const { projectId } = match.params;

    sortTarget(null, null);
    loadPage(
      projectId,
      {
        afterSuccess: () => {
          setLazyLoadingIsEnabled(true);
        },
      },
      { isReload: true, all },
    );
  }, [match, sortTarget, loadPage, all]);

  const handleSave = useCallback(() => {
    saveApproval({
      field: common.get('approvalSortField', ''),
      direction: common.get('approvalSortDirection', ''),
    });
  }, [saveApproval, common]);

  const handleEdit = useCallback(() => {
    setSavedDetails({ projectData, users, templates, common });
    reopenDetailsPopup();
  }, [projectData, users, templates, common, reopenDetailsPopup]);

  const handleExport = useCallback(() => {
    const projectId = projectData.get('id');

    downloadFile({
      url: `${config.API_BASE_URL}/api/v1/projects/${projectId}/export_universe`,
    });
  }, [projectData]);

  const handleDownload = useCallback(
    (event, { id }) => {
      const template = templates.find(templateItem => templateItem.get('id') === id);

      if (template) {
        downloadFile({
          url: template.get('url'),
          noAuth: true,
        });
      }
    },
    [templates],
  );

  const handleSomeChange = useCallback(
    (event, immutableTarget, field) => {
      const { value } = event.target;
      const target = immutableTarget.toJS();

      target[field] = value;
      changeTarget({ field, value, id: target.id });
    },
    [changeTarget],
  );

  const handleSubmit = useCallback(
    (event, approvedInfo) => {
      event.preventDefault();
      if (approvedInfo && approvedInfo.field === 'approved') {
        saveTarget({
          tbId: approvedInfo.tbId,
          id: approvedInfo.id,
          approved: approvedInfo.approved,
        });

        return;
      }

      targets.forEach(target => {
        if (!isDeepChanged(target)) return;

        const tbId = target.get('tbId');
        const id = target.get('id');
        const changed = getChanged(target);

        saveTarget({ tbId, id, ...changed });
      });
    },
    [saveTarget, targets],
  );

  const handleCeoProjectTargetChange = useCallback(
    ({ target }) => {
      const { projectId } = match.params;

      setAll(target.value);
      sortTarget(null, null);
      loadTargets(
        projectId,
        {
          page: 1,
          all: target.value,
        },
        {
          afterSuccess: () => {
            setLazyLoadingIsEnabled(true);
          },
        },
        true,
      );
    },
    [match, sortTarget, loadTargets],
  );

  useEffect(() => {
    const { projectId } = match.params;

    if (userHasAccess) {
      loadPage(projectId, {}, { all });
    } else {
      showError(openPopup, config.permisionError, () => {
        history.push('/');
        closePopup();
      });
    }
    // Mount-only: matches the original componentDidMount exactly.
  }, []);

  // Re-subscribing whenever handleScroll's own deps change (instead of attaching once via a
  // ref-captured value) keeps the listener always calling a fresh, non-stale handleScroll.
  // Capture phase catches scrolls of the layout content container (scroll doesn't bubble).
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  useEffect(() => {
    // componentDidUpdate never fires on mount; the default `errors` state is non-empty
    // (see src/reducers/project/common.js), so skipping the first run is required, not optional.
    if (hasMountedRef.current) {
      showErrorsPopup();
    } else {
      hasMountedRef.current = true;
    }
  }, [showErrorsPopup]);

  return (
    <div className="full-height">
      <Helmet
        title={`${(savedDetails?.projectData ?? projectData).getIn(['category', 'name'], '')} [Project Page]`.trim()}
      />
      <If value={userHasAccess}>
        <Project
          {...props}
          approvalColumns={approvalColumns}
          onCeoProjectTargetChange={handleCeoProjectTargetChange}
          onChange={handleSomeChange}
          onDeleteApprovalRow={handleApprovalRowDelete}
          onDownload={handleDownload}
          onEdit={handleEdit}
          onExport={handleExport}
          onNoApprovalDoubleClick={handleNoApprovalDoubleClick}
          onReload={handleReload}
          onReset={handleReset}
          onRowClick={handleRowClick}
          onRowDoubleClick={handleRowDoubleClick}
          onRowInsert={handleRowInsert}
          onSave={handleSave}
          onSort={handleSort}
          onSubmit={handleSubmit}
          onTableRef={handleTableRef}
          onTextChange={handleTextChange}
          savedDetails={savedDetails}
          targetColumns={targetColumns}
        />
      </If>
    </div>
  );
};

ProjectContainer.propTypes = {
  approvals: PropTypes.instanceOf(List).isRequired,
  changeTarget: PropTypes.func.isRequired,
  common: PropTypes.instanceOf(Map).isRequired,
  loadPage: PropTypes.func.isRequired,
  noApprovals: PropTypes.instanceOf(List).isRequired,
  projectData: PropTypes.instanceOf(Map).isRequired,
  reset: PropTypes.func.isRequired,
  saveApproval: PropTypes.func.isRequired,
  savePending: PropTypes.bool.isRequired,
  saveTarget: PropTypes.func.isRequired,
  targets: PropTypes.instanceOf(List).isRequired,
  templates: PropTypes.instanceOf(List).isRequired,
  updateApprovals: PropTypes.func.isRequired,
  user: PropTypes.instanceOf(Map),
  users: PropTypes.instanceOf(List).isRequired,
};

const mapStateToProps = state => {
  const { targets, templatePopup, ...rest } = state.project;

  return {
    ...rest,
    targets: targets.get ? targets.get('data') : targets.data,
    targetsPagination: targets.get ? targets.get('pagination') : targets.pagination,
    savePending: state.project.approvals.some(approval => approval.get('dirty')),
    templatePopup,
    user: state.auth.get('user', null),
  };
};

export { ProjectContainer };
export default connect(
  mapStateToProps,
  {
    loadTargets,
    loadSortedTargets,
    loadPage,
    reset,
    sortTarget,
    sortApproval,
    clearError,
    updateApprovals,
    insertApprovalRow,
    switchApprovalRowToEdit,
    clearTemplatePopupError,
    saveTarget,
    changeTarget,
    deleteApproval,
    saveApproval,
  },
  mergeProps,
  connectOptions,
)(memo(ProjectContainer));
